import { DataGridLeafColumn } from '@ibsys/kit';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { C2EddyCandidateItem } from '@/entities/integrations';

import { C2EddyConversationLink } from './C2EddyConversationLink';
import { C2EddyDateTime } from './C2EddyDateTime';
import { C2EddyTicketNumber } from './C2EddyTicketNumber';

/** Значения нет — ячейка остаётся пустой */
const EMPTY_CELL = '';

/**
 * Колонки превью закрытых обращений периода: номер, тема, дата закрытия, тематика и уже
 * загруженная коммуникация. Ширины в сумме укладываются в карточку интеграции и на экране ноутбука
 * (окно от 1280 px с развёрнутым меню): признак загрузки не уходит за край. Номер длиннее колонки
 * переносится, а не обрезается; дата закрытия помещается в строку на обоих языках.
 */
export const useC2EddyCandidateColumns = (): DataGridLeafColumn<C2EddyCandidateItem>[] => {
  const { t } = useTranslation(['integrations']);

  return useMemo(
    () => [
      {
        id: 'uniqueId',
        header: t('integrations:c2Eddy.candidates.uniqueId'),
        size: 110,
        wrap: true,
        cell: ({ row }) => (row.uniqueId ? <C2EddyTicketNumber value={row.uniqueId} /> : EMPTY_CELL),
      },
      {
        id: 'title',
        header: t('integrations:c2Eddy.candidates.title'),
        size: 230,
        wrap: true,
        cell: ({ row }) => row.title ?? EMPTY_CELL,
      },
      {
        id: 'closedAt',
        header: t('integrations:c2Eddy.candidates.closedAt'),
        size: 180,
        wrap: true,
        cell: ({ row }) => (row.closedAt ? <C2EddyDateTime value={row.closedAt} /> : EMPTY_CELL),
      },
      {
        id: 'topic',
        header: t('integrations:c2Eddy.candidates.topic'),
        size: 210,
        wrap: true,
        cell: ({ row }) => row.topic ?? EMPTY_CELL,
      },
      {
        id: 'conversationId',
        header: t('integrations:c2Eddy.candidates.conversationId'),
        size: 100,
        cell: ({ row }) =>
          row.conversationId === null ? (
            <span className="text-muted-foreground text-sm">{t('integrations:c2Eddy.candidates.notLoaded')}</span>
          ) : (
            <C2EddyConversationLink conversationId={row.conversationId} />
          ),
      },
    ],
    [t],
  );
};
